"use client";

import React, { useEffect, useRef } from "react";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Wrench, PenTool, Search, Waves, Flame, CircleAlert, ArrowRight } from "lucide-react";
const services = [{
  title: "Expertise SEO",
  description: "Optimisation technique et sémantique pour dominer les moteurs de recherche et capter un trafic qualifié.",
  icon: Search,
  href: "/HomePage",
  glow: "rgba(0, 240, 255, 0.15)",
  animatedBg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1646506/img/seo_matrix_bg.png"
}, {
  title: "Webmastering",
  description: "Gestion proactive et maintenance de vos plateformes digitales pour une performance et une sécurité sans faille.",
  icon: PenTool,
  href: "/HomePage",
  glow: "rgba(255, 255, 255, 0.1)",
  animatedBg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1646506/img/webmaster_grid_bg.png"
}, {
  title: "Marketing Automation",
  description: "Conception de workflows intelligents pour automatiser vos processus et décupler votre productivité opérationnelle.",
  icon: Wrench,
  href: "/HomePage",
  glow: "rgba(139, 92, 246, 0.15)",
  animatedBg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1646506/img/automation_flow_bg.png"
}, {
  title: "Intelligence Artificielle",
  description: "Intégration de solutions IA sur mesure pour transformer vos données en leviers de croissance stratégiques.",
  icon: Flame,
  href: "/HomePage",
  glow: "rgba(255, 215, 0, 0.15)",
  animatedBg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1646506/img/ia_neural_bg.png"
}, {
  title: "Stratégie Digitale",
  description: "Accompagnement 360° pour définir votre vision numérique et surpasser vos objectifs business.",
  icon: Waves,
  href: "/AboutMePage",
  glow: "rgba(0, 240, 255, 0.1)",
  animatedBg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1646506/img/strategy_data_bg.png"
}, {
  title: "Consulting Smilegeeks",
  description: "L'expertise d'une agence spécialisée au service de votre projet personnel ou entrepreneurial.",
  icon: CircleAlert,
  href: "/ContactPage",
  glow: "rgba(139, 92, 246, 0.1)",
  animatedBg: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1646506/img/consulting_tech_bg.png"
}];
export default function ExpertSolutionsForEveryPipe() {
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;
      const scrolled = window.scrollY;
      containerRef.current.style.setProperty("--scroll-y", `${scrolled * 0.05}px`);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    e.currentTarget.style.setProperty("--mouse-x", `${x}px`);
    e.currentTarget.style.setProperty("--mouse-y", `${y}px`);
  };
  return <section data-section-id="223683" id="piliers-de-croissance" className="bg-background text-foreground py-24 px-6 md:px-12 lg:px-24 overflow-hidden">
      <style dangerouslySetInnerHTML={{
      __html: `
        .scanline-effect {
          background: linear-gradient(to bottom, transparent 50%, rgba(0, 0, 0, 0.3) 50%);
          background-size: 100% 4px;
        }
      `
    }} />
      
      <div className="mx-auto max-w-7xl" ref={containerRef}>
        <div className="mb-16 max-w-2xl">
          <h2 className="font-serif text-4xl md:text-5xl font-bold tracking-tight mb-4 text-foreground">
            Piliers de Croissance
          </h2>
          <p className="font-default text-muted-foreground text-lg leading-relaxed">
            Une approche holistique du marketing digital, alliant précision technique et innovation technologique pour propulser votre présence en ligne.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
          {services.map((service, index) => {
          const Icon = service.icon;
          return <div key={service.title} data-index={index} onMouseMove={handleMouseMove} style={{
            transform: 'translateY(var(--scroll-y, 0))'
          }} className="group relative flex h-full flex-col overflow-hidden rounded-3xl border border-white/10 bg-card/40 backdrop-blur-lg transition-all duration-500 hover:scale-105 hover:bg-primary/10 hover:border-primary/40 hover:shadow-2xl hover:shadow-primary/5">
                {}
                <div className="absolute inset-0 z-0 opacity-10 transition-transform duration-700 group-hover:scale-110 group-hover:opacity-20" style={{
              backgroundImage: `url(${service.animatedBg})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }} />

                {}
                <div className="scanline-effect pointer-events-none absolute inset-0 z-0 opacity-20" />

                {}
                <div className="pointer-events-none absolute inset-0 z-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100 bg-gradient-to-br from-primary/5 via-transparent to-secondary/5" />

                {}
                <div className="pointer-events-none absolute -inset-px z-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100" style={{
              background: `radial-gradient(400px circle at var(--mouse-x, 0) var(--mouse-y, 0), ${service.glow}, transparent 60%)`
            }} />
                
                <Card className="relative z-10 flex h-full flex-col border-none bg-transparent">
                  <CardHeader className="pb-4">
                    <div className="mb-4 inline-flex size-12 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/30">
                      <Icon className="size-6" strokeWidth={1.5} />
                    </div>
                    <CardTitle className="font-serif text-2xl font-bold text-foreground tracking-tight">
                      {service.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="flex-grow pb-6">
                    <p className="font-default text-muted-foreground leading-relaxed text-base">
                      {service.description}
                    </p>
                  </CardContent>
                  <CardFooter className="pt-0 mt-auto">
                    <Link to={service.href} className="group/link inline-flex items-center gap-2 font-mono text-xs font-bold uppercase tracking-widest text-primary transition-colors hover:text-primary/80">
                      Découvrir
                      <ArrowRight className="size-4 transition-transform duration-300 group-hover/link:translate-x-1" />
                    </Link>
                  </CardFooter>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}